import React, { useContext, useMemo, useState } from 'react';

import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import HostTarget from '../../../types/host/HostTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import { useLicences } from "../../../context/LicenceContext";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import {
    CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE,
    COLOUR_HOST_MEMORY,
    COLOUR_HOST_PROCESSOR,
    DATA_LOADED,
    DATA_LOADING
} from "../../Constants";
import Helper from "../../../helpers/Helper";
import ConditionalRender from "../../../helpers/ConditionalRender";
import HostApi from "../../../api/host/HostApi";
import InstanceApi from "../../../api/instance/InstanceApi";

// Highchart modules - note, the position of these in this file are important.
require("highcharts/modules/annotations")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);

function HostsOverTimeCharts(props: { instance: InstanceTarget, hosts: HostTarget[], plotLines: ChartPlotLine[] }) {
    const [processorSeries, setProcessorSeries] = useState<number[][]>([]);
    const [memorySeries, setMemorySeries] = useState<number[][]>([]);
    const [diskSeries, setDiskSeries] = useState<number[][]>([]);
    const [diskReadPeaks, setDiskReadPeaks] = useState<number[]>([])
    const [diskWritePeaks, setDiskWritePeaks] = useState<number[]>([])
    const [loading, setLoading] = useState(DATA_LOADING)
    const {licences} = useLicences();
    const timeRangeContext = useContext(TimeRangeContext)

    const diskPeaks = useMemo(() => {
        return {
            read: Helper.getPeakValue(diskReadPeaks),
            write: Helper.getPeakValue(diskWritePeaks)
        }
    }, [diskReadPeaks, diskWritePeaks])

    const memoryPeak = useMemo(() => {
        let peaks: number[] = []
        memorySeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [memorySeries])

    const processorPeak = useMemo(() => {
        let peaks: number[] = []
        processorSeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [processorSeries])

    // Get the instance hosts processor and memory data over time.
    useMemo(() => {
        // for each host, we need to get processor, memory over time, plus their peak values.
        async function getHostsData() {
            setLoading(DATA_LOADING);
            const hosts: HostTarget[] = []
            const hostMappings = await InstanceApi.getInstanceHostByInstance(props.instance.id);
            for (let index = 0; index < hostMappings.length; index++) {
                const matchedHosts = props.hosts.filter(host => host.id === hostMappings[index].hostid);
                if (matchedHosts.length === 1) {
                    hosts.push(matchedHosts[0]);
                }
            }

            const memSeries: any[] = []
            const procSeries: any[] = []
            const diskSeries: any[] = []
            const readPeaks: number[] = []
            const writePeaks: number[] = []

            for (let index = 0; index < hosts.length; index++) {
                // Get the host we're currently dealing with.
                const host = hosts[index];

                // Get host CPU over time.
                const cpuData = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), host.id, 'cpuutilisation');
                procSeries.push({
                    name: `CPU - ${host.name}`,
                    color: COLOUR_HOST_PROCESSOR,
                    type: 'spline',
                    minHeight: 3,
                    data: cpuData.map((item) => {
                        return [getConvertedTimeToUTC(item), Math.round(item.avg)]
                    }),
                    yAxis: 0,
                    zIndex: 2
                })

                // Get host memory over time.
                const memData = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), host.id, 'memoryutilisation');
                memSeries.push({
                    name: `Memory - ${host.name}`,
                    color: COLOUR_HOST_MEMORY,
                    type: 'spline',
                    minHeight: 3,
                    data: memData.map((item) => {
                        return [getConvertedTimeToUTC(item), Math.round(item.avg)]
                    }),
                    yAxis: 0,
                    zIndex: 1
                })

                // Get host reads and writes over time.
                const metrics: string[] = ['time spent reading (ms)', 'time spent writing (ms)'];

                for (let index = 0; index < metrics.length; index++) {
                    const metric = metrics[index];
                    const diskResponseData = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), host.id, metric);
                    const diskData = diskResponseData.map(item => {
                        return [getConvertedTimeToUTC(item), Math.round(item.sum)]
                    });
                    diskSeries.push({
                        name: `${metric} - ${host.name}`,
                        color: CHART_COLOURS_STATEMENTS[index],
                        type: 'column',
                        data: diskData,
                        zIndex: 1,
                        yAxis: 0,
                    })

                    if (metric === 'time spent reading (ms)') {
                        readPeaks.push(Helper.getPeakValue(diskData.map(i => i[1])));
                    } else {
                        writePeaks.push(Helper.getPeakValue(diskData.map(i => i[1])));
                    }
                }
            }

            setMemorySeries(memSeries)
            setProcessorSeries(procSeries)
            setDiskSeries(diskSeries)
            setDiskWritePeaks(writePeaks)
            setDiskReadPeaks(readPeaks)
            setLoading(DATA_LOADED);
        }

        void getHostsData();
    }, [props.hosts, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance.id]);

    // Build the processor and memory chart.
    const processorMemoryChart = useMemo(() => {
        if (processorSeries.length && memorySeries.length) {
            return (
                <React.Fragment>
                    <span className="peak">Peak CPU {processorPeak}%, Peak Memory {memoryPeak}%</span>
                    <br/><br/>
                    <HighchartsReact constructorType={"chart"} highcharts={Highcharts}
                                     options={chartOptionsGranularity([...processorSeries, ...memorySeries], Helper.getPeakValue([processorPeak, memoryPeak]), {
                                         credits: {enabled: highchartsCredits(licences)},
                                         timeRangeContext: timeRangeContext,
                                         plotLines: props.plotLines,
                                         tooltip: {
                                             formatter: function () {
                                                 return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
                                             },
                                         },
                                         legend: 'bottom'
                                     })}/>
                </React.Fragment>
            );
        }

        return null
    }, [processorSeries, memorySeries, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.plotLines, licences, memoryPeak, processorPeak]);

    // Build the disk read and writes chart.
    const diskChart = useMemo(() => {
        if (diskSeries.length && diskPeaks.read && diskPeaks.write) {
            return (
                <React.Fragment>
                    <span className="peak">Peak Time Spent Reading {Helper.getTimeInEnglish(diskPeaks.read)}, Spent Writing {Helper.getTimeInEnglish(diskPeaks.write)}</span>
                    <br/><br/>
                    <HighchartsReact constructorType={"chart"} highcharts={Highcharts}
                                     options={chartOptionsGranularity([...diskSeries], Helper.getPeakValue([diskPeaks.read, diskPeaks.write]), {
                                         credits: {enabled: highchartsCredits(licences)},
                                         timeRangeContext: timeRangeContext,
                                         plotLines: props.plotLines,
                                         legend: 'bottom',
                                         tooltip: {
                                             formatter: function () {
                                                 return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                                             },
                                         },
                                     })}/>
                </React.Fragment>
            );
        }

        return null
    }, [diskSeries, diskPeaks, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines])

    return (
        <React.Fragment>
            <ConditionalRender if={loading === DATA_LOADING}>
                <div className="w-100 text-center text-muted mt-3 time-chart">
                    <div className="loader spinner">
                    </div>
                    <p className="mt-3">
                        Loading data...
                    </p>
                </div>
            </ConditionalRender>
            <ConditionalRender if={loading === DATA_LOADED}>
                {processorMemoryChart}
                {diskChart}
            </ConditionalRender>
        </React.Fragment>
    );
}

export default HostsOverTimeCharts;
